import type { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import * as React from 'react';

export interface PublicAuthConfig {
  tenantId: string;
  tenantName: string;
  clientId: string;
  authority: string;
  scope: string;
}

interface AuthContextValue {
  loading: boolean;
  configured: boolean;
  account: AccountInfo | null;
  error: Error | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  apiFetch: <T>(path: string, init?: RequestInit) => Promise<T>;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export const requiresInteraction = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { name?: unknown; errorCode?: unknown };
  return (
    candidate.name === 'InteractionRequiredAuthError' ||
    ['interaction_required', 'consent_required', 'login_required'].includes(
      typeof candidate.errorCode === 'string' ? candidate.errorCode : '',
    )
  );
};

export const readJson = async <T,>(response: Response): Promise<T> => {
  const body = (await response.json()) as T & {
    error?: { message?: string };
  };
  if (!response.ok) {
    throw new Error(body.error?.message || `Request failed with HTTP ${response.status}`);
  }
  return body;
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [client, setClient] = React.useState<PublicClientApplication | null>(null);
  const [config, setConfig] = React.useState<PublicAuthConfig | null>(null);
  const [account, setAccount] = React.useState<AccountInfo | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    let alive = true;
    const initialize = async () => {
      try {
        const response = await fetch('/api/auth/config', {
          headers: { Accept: 'application/json' },
        });
        if (response.status === 404 || response.status === 503) {
          if (alive) setLoading(false);
          return;
        }
        const body = await readJson<{ data: PublicAuthConfig }>(response);
        const authConfig = body.data;
        const { PublicClientApplication } = await import('@azure/msal-browser');
        const msal = new PublicClientApplication({
          auth: {
            clientId: authConfig.clientId,
            authority: authConfig.authority,
            knownAuthorities: [`${authConfig.tenantName}.ciamlogin.com`],
            redirectUri: `${window.location.origin}/`,
            postLogoutRedirectUri: `${window.location.origin}/`,
          },
          cache: {
            cacheLocation: 'sessionStorage',
          },
        });
        await msal.initialize();
        const redirect = await msal.handleRedirectPromise();
        const activeAccount = redirect?.account ?? msal.getAllAccounts()[0] ?? null;
        if (activeAccount) msal.setActiveAccount(activeAccount);
        if (alive) {
          setClient(msal);
          setConfig(authConfig);
          setAccount(activeAccount);
        }
      } catch (initializationError) {
        if (alive) {
          setError(
            initializationError instanceof Error
              ? initializationError
              : new Error(String(initializationError)),
          );
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    initialize();
    return () => {
      alive = false;
    };
  }, []);

  const signIn = React.useCallback(async () => {
    if (!client || !config) throw new Error('Provider sign-in is not configured');
    await client.loginRedirect({
      scopes: [config.scope, 'openid', 'profile', 'email'],
      prompt: 'select_account',
    });
  }, [client, config]);

  const signOut = React.useCallback(async () => {
    if (!client) return;
    await client.logoutRedirect({ account: account ?? undefined });
  }, [account, client]);

  const apiFetch = React.useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
      if (!client || !config || !account) {
        throw new Error('Sign in to continue');
      }
      let accessToken: string;
      try {
        const token = await client.acquireTokenSilent({
          account,
          scopes: [config.scope],
        });
        accessToken = token.accessToken;
      } catch (tokenError) {
        if (requiresInteraction(tokenError)) {
          await client.acquireTokenRedirect({
            account,
            scopes: [config.scope],
          });
          throw new Error('Sign-in confirmation is required');
        }
        throw tokenError;
      }
      const headers = new Headers(init.headers);
      headers.set('X-FMC-Access-Token', accessToken);
      headers.set('Accept', 'application/json');
      if (init.body !== undefined) headers.set('Content-Type', 'application/json');
      return readJson<T>(
        await fetch(path, {
          ...init,
          headers,
        }),
      );
    },
    [account, client, config],
  );

  const value = React.useMemo<AuthContextValue>(
    () => ({
      loading,
      configured: Boolean(client && config),
      account,
      error,
      signIn,
      signOut,
      apiFetch,
    }),
    [account, apiFetch, client, config, error, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const value = React.useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
};
